import { useEffect, useRef, useState } from "react";
import { FileSpreadsheet, Upload } from "lucide-react";
import { Loader } from "@/components/shared/loader";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ObsValidationReportView } from "./ObsValidationReport";
import {
  useUploadObsWorkbook,
  useValidateObsWorkbook,
} from "@/features/obs/hooks/useObsQueries";
import { handleApiError } from "@/lib/error-handler";
import type {
  ObsScreen,
  ObsUploadResult,
  ObsValidationReport,
} from "@/types/obs";

/**
 * Replace one screen's data.
 *
 * The flow is deliberately two-step — CHECK, then PUBLISH — and never one
 * button. These are the national budget figures on a public site; the operator
 * has to see what a file would change before it changes it. The dry run touches
 * nothing on the server, so backing out costs nothing.
 *
 * Publishing re-validates server-side rather than trusting this dialog's earlier
 * verdict: the check and the publish are separate requests, and only the server
 * decides.
 */
export function ObsUploadDialog({
  screen,
  open,
  onOpenChange,
}: {
  screen: ObsScreen;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [report, setReport] = useState<ObsValidationReport | null>(null);
  const [published, setPublished] = useState<ObsUploadResult | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const validate = useValidateObsWorkbook();
  const upload = useUploadObsWorkbook();

  // Reset when reopened, or the previous file's verdict would be shown against
  // a new one — the most dangerous possible stale state on this screen.
  useEffect(() => {
    if (!open) return;
    setFile(null);
    setReport(null);
    setPublished(null);
    validate.reset();
    upload.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const pick = (next: File | null) => {
    setFile(next);
    setReport(null);
    setPublished(null);
  };

  const runCheck = async () => {
    if (!file) return;
    try {
      setReport(await validate.mutateAsync({ slug: screen.slug, file }));
    } catch (e) {
      handleApiError(e);
    }
  };

  const runPublish = async () => {
    if (!file) return;
    try {
      // force=true only ever acknowledges WARNINGS. The server refuses errors
      // regardless, so this cannot be used to push a broken workbook through.
      const result = await upload.mutateAsync({
        slug: screen.slug,
        file,
        force: true,
      });
      if (result.applied) setPublished(result);
      else setReport(result);
    } catch (e) {
      handleApiError(e);
    }
  };

  const busy = validate.isPending || upload.isPending;
  const canPublish = !!report?.accepted && !busy;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>رفع بيانات — {screen.title.ar}</DialogTitle>
          <DialogDescription>
            اختر ملف Excel، ثم افحصه قبل النشر. الفحص لا يغيّر أي شيء على
            الموقع.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-lg border border-dashed border-border p-4">
            <div className="flex items-center gap-3">
              <FileSpreadsheet className="size-8 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <Input
                  ref={inputRef}
                  type="file"
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  disabled={busy}
                  onChange={(e) => pick(e.target.files?.[0] ?? null)}
                />
                <p className="mt-2 text-xs text-muted-foreground">
                  الملف المتوقّع:{" "}
                  <span dir="ltr" className="font-mono">
                    {screen.expectedWorkbook}
                  </span>
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  يجب أن تبقى أسماء الأوراق والأعمدة والتسميات كما هي. يمكن
                  إضافة سنوات أو جهات أو صفوف جديدة بحرية.
                </p>
              </div>
            </div>
          </div>

          {published ? (
            <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-4">
              <div className="font-semibold">
                تم النشر — الإصدار {published.dataset?.version}
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                الشاشة على الموقع تعرض هذه البيانات الآن. الإصدار السابق محفوظ
                ويمكن الرجوع إليه.
              </p>
            </div>
          ) : (
            report && (
              <ObsValidationReportView
                report={report}
                previousStats={screen.active?.stats ?? null}
              />
            )
          )}
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            {published ? "إغلاق" : "إلغاء"}
          </Button>

          {!published && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={runCheck}
                disabled={!file || busy}
              >
                {validate.isPending ? <Loader /> : null}
                فحص الملف
              </Button>
              <Button onClick={runPublish} disabled={!canPublish}>
                {upload.isPending ? <Loader /> : <Upload className="size-4" />}
                نشر
              </Button>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
